<template>
  <ContentWrap title="酒店运营概况">
    <div v-loading="pageLoading" class="hotel-dashboard">
      <div class="hotel-intro">
        <div>
          <div class="intro-title">酒店管理，覆盖预订、房态、入住、退房、会员与经营分析的一体化数字化中台。</div>
        </div>
      </div>

      <el-row :gutter="16" class="design-row">
        <el-col v-for="item in designCards" :key="item.key" :xs="24" :lg="12">
          <div class="design-card" @click="openDesignPage(item.path)">
            <div class="design-copy">
              <div class="design-icon" :style="{ color: item.color, background: item.bg }">
                <el-icon :size="24">
                  <component :is="item.icon" />
                </el-icon>
              </div>
              <div>
                <div class="design-title">{{ item.title }}</div>
                <div class="design-desc">{{ item.desc }}</div>
              </div>
            </div>
            <div class="design-device" :class="`design-device-${item.preview}`">
              <img :src="item.image" :alt="item.title" />
            </div>
          </div>
        </el-col>
      </el-row>

      <div class="dashboard-toolbar">
        <div class="toolbar-title">
          <div class="toolbar-heading">数据分析</div>
          <div class="toolbar-subtitle">客房状态、入住退房、营收趋势与待办告警</div>
        </div>
        <div class="toolbar-actions">
          <el-select
            v-model="queryParams.shopId"
            placeholder="全部店铺"
            clearable
            class="shop-select"
            @change="loadAllData"
          >
            <el-option
              v-for="item in shopList"
              :key="item.id"
              :label="item.name"
              :value="item.id"
            />
          </el-select>
          <el-date-picker
            v-model="queryParams.statDate"
            type="date"
            placeholder="选择日期"
            value-format="YYYY-MM-DD"
            class="date-picker"
            @change="loadStats"
          />
          <el-button type="primary" :loading="pageLoading" @click="loadAllData">
            <el-icon><Refresh /></el-icon>
            刷新
          </el-button>
        </div>
      </div>

      <el-row :gutter="16" class="metric-row">
        <el-col v-for="item in metricCards" :key="item.key" :xs="24" :sm="12" :lg="6">
          <div class="metric-card" :style="{ '--accent-color': item.color }">
            <div class="metric-icon">
              <el-icon :size="22">
                <component :is="item.icon" />
              </el-icon>
            </div>
            <div class="metric-content">
              <div class="metric-label">{{ item.label }}</div>
              <div class="metric-value">{{ item.value }}</div>
              <div class="metric-note">{{ item.note }}</div>
            </div>
          </div>
        </el-col>
      </el-row>

      <el-row :gutter="16" class="overview-row">
        <el-col :xs="24" :lg="8">
          <div class="panel room-panel">
            <div class="panel-header">
              <div>
                <div class="panel-title">房态总览</div>
                <div class="panel-subtitle">当前可售与占用情况</div>
              </div>
              <el-tag type="success" effect="plain">{{ formatPercent(stats.occupancyRate) }}</el-tag>
            </div>
            <div class="room-summary">
              <div class="room-total">{{ stats.totalRooms }}</div>
              <div class="room-label">总房间数</div>
            </div>
            <el-progress
              :percentage="roomOccupiedPercent"
              :stroke-width="10"
              color="#2f80ed"
              :show-text="false"
            />
            <div class="room-status-list">
              <div class="room-status-item">
                <span class="status-dot status-dot-occupied"></span>
                <span>已占用</span>
                <strong>{{ stats.occupiedRooms }}</strong>
              </div>
              <div class="room-status-item">
                <span class="status-dot status-dot-available"></span>
                <span>空闲</span>
                <strong>{{ stats.availableRooms }}</strong>
              </div>
              <div class="room-status-item">
                <span class="status-dot status-dot-booked"></span>
                <span>预订中</span>
                <strong>{{ stats.bookedRooms }}</strong>
              </div>
              <div class="room-status-item">
                <span class="status-dot status-dot-checkout"></span>
                <span>退房待清洁</span>
                <strong>{{ stats.checkoutRooms }}</strong>
              </div>
              <div class="room-status-item">
                <span class="status-dot status-dot-blocked"></span>
                <span>清洁/维修/锁定</span>
                <strong>{{ stats.unavailableRooms }}</strong>
              </div>
              <div class="room-status-item">
                <span class="status-dot status-dot-live"></span>
                <span>在住</span>
                <strong>{{ stats.currentOccupancy }}</strong>
              </div>
            </div>
          </div>
        </el-col>

        <el-col :xs="24" :lg="16">
          <div class="panel todo-panel">
            <div class="panel-header">
              <div>
                <div class="panel-title">待办与风险</div>
                <div class="panel-subtitle">需要门店及时处理的事项</div>
              </div>
            </div>
            <el-row :gutter="12">
              <el-col v-for="item in todoItems" :key="item.key" :xs="24" :sm="8">
                <div class="todo-item" :class="`todo-item-${item.type}`">
                  <div class="todo-icon">
                    <el-icon :size="22">
                      <component :is="item.icon" />
                    </el-icon>
                  </div>
                  <div>
                    <div class="todo-label">{{ item.label }}</div>
                    <div class="todo-value">{{ item.value }}</div>
                  </div>
                </div>
              </el-col>
            </el-row>
            <div class="booking-strip">
              <div class="booking-item">
                <span>新增预订</span>
                <strong>{{ stats.newBookingCount }}</strong>
              </div>
              <div class="booking-item">
                <span>取消订单</span>
                <strong>{{ stats.cancelCount }}</strong>
              </div>
              <div class="booking-item">
                <span>净增预订</span>
                <strong>{{ netBookingCount }}</strong>
              </div>
            </div>
          </div>
        </el-col>
      </el-row>

      <el-row :gutter="16" class="chart-row">
        <el-col :xs="24" :lg="12">
          <div class="panel chart-panel">
            <div class="panel-header">
              <div>
                <div class="panel-title">入住率趋势</div>
                <div class="panel-subtitle">近 30 天</div>
              </div>
            </div>
            <div ref="occupancyChartRef" class="chart-box"></div>
          </div>
        </el-col>
        <el-col :xs="24" :lg="12">
          <div class="panel chart-panel">
            <div class="panel-header">
              <div>
                <div class="panel-title">营收趋势</div>
                <div class="panel-subtitle">近 30 天</div>
              </div>
            </div>
            <div ref="revenueChartRef" class="chart-box"></div>
          </div>
        </el-col>
      </el-row>
    </div>
  </ContentWrap>
</template>

<script setup lang="ts">
import { computed, ref, reactive, onMounted, onUnmounted, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import {
  Refresh,
  UserFilled,
  Finished,
  Money,
  TrendCharts,
  WarningFilled,
  Tools,
  BellFilled,
  Cellphone
} from '@element-plus/icons-vue'
import * as echarts from 'echarts'
import { getShopSimpleList } from '@/api/hotel/platformShop'
import * as DashboardApi from '@/api/hotel/dashboard'
import type { DashboardStatsVO, TrendItemVO } from '@/api/hotel/dashboard'
import * as DiyTemplateApi from '@/api/diy/template'
import hotelMobileDesignPreview from '@/assets/imgs/hotel-mobile-design-preview.svg'

defineOptions({ name: 'HotelDashboard' })

const router = useRouter()

// ==================== 查询参数 ====================
const queryParams = reactive({
  shopId: undefined as string | number | undefined,
  statDate: ''
})

const pageLoading = ref(false)

// ==================== 店铺列表 ====================
const shopList = ref<any[]>([])

const loadShopList = async () => {
  try {
    shopList.value = (await getShopSimpleList()) || []
  } catch (error) {
    console.error('Failed to load hotel shop list', error)
  }
}

// ==================== 统计数据 ====================
const stats = reactive<DashboardStatsVO>({
  shopId: '',
  statDate: '',
  totalRooms: 0,
  occupiedRooms: 0,
  availableRooms: 0,
  bookedRooms: 0,
  checkoutRooms: 0,
  unavailableRooms: 0,
  checkInCount: 0,
  checkOutCount: 0,
  currentOccupancy: 0,
  newBookingCount: 0,
  cancelCount: 0,
  revenue: 0,
  occupancyRate: 0,
  cleaningCount: 0,
  maintenanceCount: 0,
  alertCount: 0
})

const roomOccupiedPercent = computed(() => {
  if (!stats.totalRooms) return 0
  return Math.min(100, Math.round((stats.occupiedRooms / stats.totalRooms) * 100))
})

const netBookingCount = computed(() => stats.newBookingCount - stats.cancelCount)

const metricCards = computed(() => [
  {
    key: 'revenue',
    label: '今日营收',
    value: `¥${formatMoney(stats.revenue)}`,
    note: '已入账订单金额',
    icon: Money,
    color: '#2f80ed'
  },
  {
    key: 'occupancyRate',
    label: '入住率',
    value: formatPercent(stats.occupancyRate),
    note: `${stats.occupiedRooms}/${stats.totalRooms} 间已占用`,
    icon: TrendCharts,
    color: '#00a67d'
  },
  {
    key: 'checkIn',
    label: '今日入住',
    value: formatInteger(stats.checkInCount),
    note: `当前在住 ${formatInteger(stats.currentOccupancy)}`,
    icon: UserFilled,
    color: '#7c5cff'
  },
  {
    key: 'checkOut',
    label: '今日退房',
    value: formatInteger(stats.checkOutCount),
    note: `空闲房间 ${formatInteger(stats.availableRooms)}`,
    icon: Finished,
    color: '#f59f00'
  }
])

const todoItems = computed(() => [
  {
    key: 'cleaning',
    label: '清洁待处理',
    value: formatInteger(stats.cleaningCount),
    icon: WarningFilled,
    type: 'warning'
  },
  {
    key: 'maintenance',
    label: '维修待处理',
    value: formatInteger(stats.maintenanceCount),
    icon: Tools,
    type: 'danger'
  },
  {
    key: 'alert',
    label: '风险告警',
    value: formatInteger(stats.alertCount),
    icon: BellFilled,
    type: 'danger'
  }
])

const designCards = [
  {
    key: 'mobile',
    title: '酒店移动端设计',
    desc: '参考现有酒店移动端首页，覆盖城市搜索、入住日期、快捷服务和酒店卡片。',
    path: '/diy/template/editor',
    icon: Cellphone,
    color: '#00a67d',
    bg: '#eafaf5',
    preview: 'mobile',
    image: hotelMobileDesignPreview
  }
]

const hotelDesignTemplateId = ref<number>()

const readTemplateFlag = (value: unknown) => {
  return value === true || value === 1 || value === '1' || value === 'true' || value === '\u0001'
}

const loadHotelDesignTemplate = async () => {
  try {
    const data = await DiyTemplateApi.getDiyTemplatePage({
      pageNo: 1,
      pageSize: 100,
      industry: 'hotel',
      scene: 'home'
    })
    const templates = (data?.list || []) as DiyTemplateApi.DiyTemplateVO[]
    const editableTemplates = templates.filter((item) => {
      const isDefault =
        readTemplateFlag(item.defaultTemplate) ||
        readTemplateFlag(item.isDefault) ||
        Boolean(item.name?.includes('默认'))
      return item.id && !isDefault
    })
    const appliedTemplate = editableTemplates.find(
      (item) => readTemplateFlag(item.applied) || readTemplateFlag(item.isApplied)
    )
    hotelDesignTemplateId.value = appliedTemplate?.id || editableTemplates[0]?.id
  } catch (error) {
    hotelDesignTemplateId.value = undefined
    console.error('Failed to load hotel design template', error)
  }
}

const openDesignPage = (path: string) => {
  if (path === '/diy/template/editor') {
    const target = hotelDesignTemplateId.value
      ? `${path}/${hotelDesignTemplateId.value}`
      : { path: '/diy/template/list', query: { industry: 'hotel', scene: 'home' } }
    const { href } = router.resolve(target)
    window.open(href, '_blank', 'noopener,noreferrer')
    if (!hotelDesignTemplateId.value) {
      ElMessage.warning('暂无可设计的酒店模板，请先在模板管理中新建模板')
    }
    return
  }
  router.push(path)
}

const loadStats = async () => {
  try {
    const data = await DashboardApi.getDashboardStats({
      shopId: queryParams.shopId,
      statDate: queryParams.statDate
    })
    if (data) {
      Object.assign(stats, data)
    }
  } catch (error) {
    console.error('Failed to load hotel dashboard stats', error)
  }
}

// ==================== 图表 ====================
const occupancyChartRef = ref<HTMLDivElement>()
const revenueChartRef = ref<HTMLDivElement>()
let occupancyChart: echarts.ECharts | null = null
let revenueChart: echarts.ECharts | null = null

const initCharts = () => {
  if (occupancyChartRef.value) {
    occupancyChart = echarts.init(occupancyChartRef.value)
  }
  if (revenueChartRef.value) {
    revenueChart = echarts.init(revenueChartRef.value)
  }
  window.addEventListener('resize', handleResize)
}

const handleResize = () => {
  occupancyChart?.resize()
  revenueChart?.resize()
}

const loadOccupancyTrend = async () => {
  try {
    const data = await DashboardApi.getOccupancyTrend({
      shopId: queryParams.shopId
    })
    setOccupancyChart(data || [])
  } catch (error) {
    console.error('Failed to load hotel occupancy trend', error)
  }
}

const loadRevenueTrend = async () => {
  try {
    const data = await DashboardApi.getRevenueTrend({
      shopId: queryParams.shopId
    })
    setRevenueChart(data || [])
  } catch (error) {
    console.error('Failed to load hotel revenue trend', error)
  }
}

const setOccupancyChart = (data: TrendItemVO[]) => {
  if (!occupancyChart) return
  occupancyChart.setOption({
    tooltip: {
      trigger: 'axis',
      formatter: (params: any) => {
        const item = params[0]
        return `${item.axisValue}<br/>入住率: ${item.value}%`
      }
    },
    grid: {
      top: 20,
      right: 20,
      bottom: 30,
      left: 50
    },
    xAxis: {
      type: 'category',
      data: data.map((d) => d.date),
      axisLabel: {
        rotate: 45,
        fontSize: 11
      }
    },
    yAxis: {
      type: 'value',
      name: '入住率(%)',
      min: 0,
      max: 100
    },
    series: [
      {
        name: '入住率',
        type: 'line',
        data: data.map((d) => d.value),
        smooth: true,
        symbol: 'circle',
        symbolSize: 6,
        lineStyle: {
          color: '#409eff',
          width: 2
        },
        itemStyle: {
          color: '#409eff'
        },
        areaStyle: {
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            { offset: 0, color: 'rgba(64, 158, 255, 0.3)' },
            { offset: 1, color: 'rgba(64, 158, 255, 0.05)' }
          ])
        }
      }
    ]
  })
}

const setRevenueChart = (data: TrendItemVO[]) => {
  if (!revenueChart) return
  revenueChart.setOption({
    tooltip: {
      trigger: 'axis',
      formatter: (params: any) => {
        const item = params[0]
        return `${item.axisValue}<br/>营收: ¥${item.value.toFixed(2)}`
      }
    },
    grid: {
      top: 20,
      right: 20,
      bottom: 30,
      left: 60
    },
    xAxis: {
      type: 'category',
      data: data.map((d) => d.date),
      axisLabel: {
        rotate: 45,
        fontSize: 11
      }
    },
    yAxis: {
      type: 'value',
      name: '营收(元)'
    },
    series: [
      {
        name: '营收',
        type: 'bar',
        data: data.map((d) => d.value),
        barWidth: '50%',
        itemStyle: {
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            { offset: 0, color: '#67c23a' },
            { offset: 1, color: '#95d475' }
          ]),
          borderRadius: [4, 4, 0, 0]
        }
      }
    ]
  })
}

// ==================== 数据加载 ====================
const loadAllData = async () => {
  pageLoading.value = true
  try {
    await Promise.all([
      loadStats(),
      loadOccupancyTrend(),
      loadRevenueTrend(),
      loadHotelDesignTemplate()
    ])
  } finally {
    pageLoading.value = false
  }
}

// ==================== 格式化 ====================
const formatMoney = (value: number): string => {
  if (value === null || value === undefined) return '0.00'
  return value.toFixed(2)
}

const formatInteger = (value: number): string => {
  if (value === null || value === undefined) return '0'
  return String(value)
}

const formatPercent = (value: number): string => {
  if (value === null || value === undefined) return '0.00%'
  return value.toFixed(2) + '%'
}

// ==================== 生命周期 ====================
onMounted(() => {
  loadShopList()
  nextTick(() => {
    initCharts()
  })
  loadAllData()
})

onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
  occupancyChart?.dispose()
  revenueChart?.dispose()
})
</script>

<style scoped lang="scss">
.hotel-dashboard {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.dashboard-toolbar,
.panel,
.metric-card,
.hotel-intro,
.design-card {
  border: 1px solid #e8ecf2;
  border-radius: 8px;
  background: #fff;
}

.hotel-intro {
  min-height: 126px;
  padding: 22px 24px;
  display: flex;
  justify-content: space-between;
  gap: 18px;
  align-items: center;
  background:
    linear-gradient(90deg, rgba(47, 128, 237, 0.08), rgba(0, 166, 125, 0.06)),
    #fff;
}

.intro-kicker {
  margin-bottom: 8px;
  font-size: 12px;
  font-weight: 700;
  color: #2f80ed;
}

.intro-title {
  max-width: 820px;
  font-size: 20px;
  line-height: 30px;
  font-weight: 700;
  color: #1f2a3d;
}

.design-row {
  row-gap: 16px;
}

.design-card {
  min-height: 168px;
  padding: 18px;
  display: flex;
  justify-content: space-between;
  gap: 18px;
  cursor: pointer;
  transition: border-color 0.2s, box-shadow 0.2s, transform 0.2s;
}

.design-card:hover {
  border-color: #2f80ed;
  box-shadow: 0 10px 26px rgba(31, 42, 61, 0.08);
  transform: translateY(-2px);
}

.design-copy {
  display: flex;
  gap: 14px;
  min-width: 0;
}

.design-icon {
  width: 46px;
  height: 46px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 46px;
}

.design-title {
  margin-top: 2px;
  font-size: 16px;
  font-weight: 700;
  color: #202532;
}

.design-desc {
  max-width: 420px;
  margin-top: 8px;
  font-size: 13px;
  line-height: 20px;
  color: #687386;
}

.design-device {
  flex: 0 0 210px;
  height: 126px;
  border-radius: 8px;
  overflow: hidden;
  background: #f6f8fb;
  border: 1px solid #edf1f6;
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.4);
}

.design-device-mobile {
  flex-basis: 128px;
}

.design-device img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.dashboard-toolbar {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  padding: 16px;
  align-items: center;
}

.toolbar-heading {
  font-size: 18px;
  font-weight: 700;
  color: #202532;
}

.toolbar-subtitle,
.panel-subtitle,
.metric-note {
  margin-top: 4px;
  font-size: 12px;
  color: #8a93a3;
}

.toolbar-actions {
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
}

.shop-select {
  width: 220px;
}

.date-picker {
  width: 160px;
}

.metric-row,
.overview-row,
.chart-row {
  row-gap: 16px;
}

.metric-card {
  min-height: 126px;
  padding: 18px;
  display: flex;
  gap: 14px;
  border-top: 3px solid var(--accent-color);
}

.metric-icon,
.todo-icon {
  width: 42px;
  height: 42px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 42px;
}

.metric-icon {
  color: var(--accent-color);
  background: color-mix(in srgb, var(--accent-color) 12%, #fff);
}

.metric-content {
  min-width: 0;
}

.metric-label,
.todo-label,
.room-label {
  font-size: 13px;
  color: #687386;
}

.metric-value {
  margin-top: 6px;
  font-size: 26px;
  line-height: 32px;
  font-weight: 700;
  color: #1f2a3d;
  word-break: break-all;
}

.panel {
  height: 100%;
  padding: 18px;
}

.panel-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
}

.panel-title {
  font-size: 15px;
  font-weight: 700;
  color: #202532;
}

.room-summary {
  margin: 8px 0 14px;
}

.room-total {
  font-size: 34px;
  line-height: 40px;
  font-weight: 700;
  color: #1f2a3d;
}

.room-status-list {
  display: grid;
  gap: 10px;
  margin-top: 18px;
}

.room-status-item,
.booking-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  font-size: 13px;
  color: #687386;
}

.room-status-item strong,
.booking-item strong {
  font-size: 16px;
  color: #202532;
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex: 0 0 8px;
}

.status-dot-occupied {
  background: #2f80ed;
}

.status-dot-available {
  background: #00a67d;
}

.status-dot-booked {
  background: #f59f00;
}

.status-dot-checkout {
  background: #e8590c;
}

.status-dot-blocked {
  background: #868e96;
}

.status-dot-live {
  background: #7c5cff;
}

.todo-item {
  display: flex;
  gap: 12px;
  align-items: center;
  min-height: 96px;
  padding: 16px;
  border-radius: 8px;
  background: #f7f9fc;
}

.todo-item-warning .todo-icon {
  color: #d98e00;
  background: #fff7e6;
}

.todo-item-danger .todo-icon {
  color: #d64545;
  background: #fff1f1;
}

.todo-value {
  margin-top: 6px;
  font-size: 24px;
  line-height: 30px;
  font-weight: 700;
  color: #202532;
}

.booking-strip {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px solid #eef1f5;
}

.booking-item {
  min-height: 48px;
  padding: 0 12px;
  border-radius: 8px;
  background: #fafbfd;
}

.chart-box {
  width: 100%;
  height: 320px;
}

@media (max-width: 900px) {
  .hotel-intro,
  .dashboard-toolbar {
    align-items: stretch;
    flex-direction: column;
  }

  .intro-title {
    font-size: 18px;
    line-height: 28px;
  }

  .design-card {
    flex-direction: column;
  }

  .design-device {
    width: 100%;
    flex-basis: auto;
  }

  .toolbar-actions,
  .shop-select,
  .date-picker {
    width: 100%;
  }

  .booking-strip {
    grid-template-columns: 1fr;
  }
}
</style>
